import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../lib/axios';

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

export interface HomeAppearanceConfig {
  backgroundTheme: string;
  backgroundThemeIntensity: number;
}

export interface HomeAppearanceOptions {
  backgroundThemes: string[];
  intensity: {
    min: number;
    max: number;
    default: number;
  };
}

export interface HomeAppearanceResponse {
  config: HomeAppearanceConfig;
  options: HomeAppearanceOptions;
}

export const homeAppearanceKeys = {
  all: ['home-appearance'] as const,
  detail: () => [...homeAppearanceKeys.all, 'detail'] as const,
};

const fetchHomeAppearance = async (): Promise<HomeAppearanceResponse> => {
  const response = await axiosInstance.get<ApiResponse<HomeAppearanceResponse>>(
    '/v1/admin/home-appearance'
  );

  if (!response.data.data) {
    throw new Error('Home appearance response is empty');
  }

  return response.data.data;
};

const updateHomeAppearance = async (
  config: HomeAppearanceConfig
): Promise<HomeAppearanceResponse> => {
  const response = await axiosInstance.put<ApiResponse<HomeAppearanceResponse>>(
    '/v1/admin/home-appearance',
    config
  );

  if (!response.data.data) {
    throw new Error(response.data.message || 'Home appearance response is empty');
  }

  return response.data.data;
};

export const useHomeAppearance = () => {
  return useQuery({
    queryKey: homeAppearanceKeys.detail(),
    queryFn: fetchHomeAppearance,
    staleTime: 60 * 1000,
  });
};

export const useUpdateHomeAppearance = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateHomeAppearance,
    onSuccess: (data) => {
      queryClient.setQueryData(homeAppearanceKeys.detail(), data);
      queryClient.invalidateQueries({ queryKey: homeAppearanceKeys.detail() });
    },
  });
};
